import React, { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faXmark, faExternalLink } from '@fortawesome/free-solid-svg-icons';
import PaginationButtons from '../../../components/Common/Pagination';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import { debounce } from '../../../helper/Debounce';
import { getSchoolTeachers, TeacherListItem } from './services/School.service';

interface TeachersDialogProps {
  open: boolean;
  schoolId: string;
  schoolName: string;
  onClose: () => void;
  onNavigateToCustomer?: (email: string) => void;
}

const formatDate = (d: any): string => {
  if (!d) return '-';
  const date = typeof d === 'string' ? new Date(d) : d._seconds ? new Date(d._seconds * 1000) : new Date(d);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const TeachersDialog: React.FC<TeachersDialogProps> = ({
  open,
  schoolId,
  schoolName,
  onClose,
  onNavigateToCustomer,
}) => {
  const [teachers, setTeachers] = useState<TeacherListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchText, setSearchText] = useState('');
  const cancelButtonRef = useRef(null);

  const loadTeachers = useCallback(
    async (page: number, search?: string) => {
      if (!schoolId) return;
      setLoading(true);
      try {
        const result = await getSchoolTeachers(schoolId, page, 25, search || undefined);
        setTeachers(result.teachers);
        setTotalPages(result.totalPages);
      } finally {
        setLoading(false);
      }
    },
    [schoolId]
  );

  useEffect(() => {
    if (open && schoolId) {
      setCurrentPage(1);
      setSearchText('');
      loadTeachers(1);
    }
  }, [open, schoolId]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((text: string) => {
      setCurrentPage(1);
      loadTeachers(1, text);
    }, 500),
    [loadTeachers]
  );

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    if (value.length >= 2 || value.length === 0) {
      debouncedSearch(value);
    }
  };

  const handlePageChange = (page: number | string) => {
    const p = typeof page === 'string' ? parseInt(page, 10) : page;
    if (p > 0 && p <= totalPages) {
      setCurrentPage(p);
      loadTeachers(p, searchText || undefined);
    }
  };

  const handleViewCustomer = (email: string) => {
    onClose();
    onNavigateToCustomer?.(email);
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-30"
        initialFocus={cancelButtonRef}
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-30 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-all sm:w-full sm:max-w-2xl">
                {/* Header */}
                <div className="flex items-center justify-between border-b px-6 py-4">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    Teachers — {schoolName}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    ref={cancelButtonRef}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
                  </button>
                </div>

                {/* Search */}
                <div className="px-6 pt-4 pb-2">
                  <div className="relative">
                    <FontAwesomeIcon
                      icon={faMagnifyingGlass}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"
                    />
                    <input
                      type="text"
                      placeholder="Search by name or email..."
                      value={searchText}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      className="border border-gray-300 rounded-md p-2 pl-9 w-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="px-6 pb-4" style={{ minHeight: '300px' }}>
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner />
                    </div>
                  ) : teachers.length === 0 ? (
                    <div className="text-center py-12 text-gray-500 text-sm">
                      No teachers linked to this school
                    </div>
                  ) : (
                    <table className="min-w-full border border-gray-300 mt-2">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="text-left py-2 px-4 border-b text-xs font-semibold text-gray-600 uppercase">
                            Email
                          </th>
                          <th className="text-left py-2 px-4 border-b text-xs font-semibold text-gray-600 uppercase">
                            First Name
                          </th>
                          <th className="text-left py-2 px-4 border-b text-xs font-semibold text-gray-600 uppercase">
                            Last Name
                          </th>
                          <th className="text-left py-2 px-4 border-b text-xs font-semibold text-gray-600 uppercase">
                            Joined
                          </th>
                          <th className="text-left py-2 px-4 border-b text-xs font-semibold text-gray-600 uppercase w-16">
                            View
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {teachers.map((teacher, i) => (
                          <tr key={teacher.email} className={i % 2 === 0 ? 'bg-gray-50' : ''}>
                            <td className="text-left py-2 px-4 border-b text-sm">{teacher.email}</td>
                            <td className="text-left py-2 px-4 border-b text-sm">{teacher.firstName || '-'}</td>
                            <td className="text-left py-2 px-4 border-b text-sm">{teacher.lastName || '-'}</td>
                            <td className="text-left py-2 px-4 border-b text-xs text-gray-500">
                              {formatDate(teacher.joinedAt)}
                            </td>
                            <td className="text-left py-2 px-4 border-b">
                              {onNavigateToCustomer && (
                                <button
                                  onClick={() => handleViewCustomer(teacher.email)}
                                  className="text-blue-600 hover:text-blue-800"
                                  title="View in Customers"
                                >
                                  <FontAwesomeIcon icon={faExternalLink} className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-end px-6 pb-4">
                    <PaginationButtons
                      totalPages={totalPages}
                      currentPage={currentPage}
                      handlePageChange={handlePageChange}
                    />
                  </div>
                )}

                {/* Footer */}
                <div className="flex justify-end border-t px-6 py-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default TeachersDialog;
